import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { logErr } from "../lib/utils-trel";
import { Star, MapPin, Clock, Users, Check, ShieldCheck, CalendarCheck2, AlertTriangle } from "lucide-react";
import SafeExperienceImg from "../components/SafeExperienceImg";
import TrustBadges from "../components/TrustBadges";
import { toast } from "sonner";

export default function ExperienceDetail(){
  const { id } = useParams();
  const [params] = useSearchParams();
  const referralHost = params.get("host") || null;
  const nav = useNavigate();
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [endTime, setEndTime] = useState("22:00");
  const [guests, setGuests] = useState(2);
  const [occasion, setOccasion] = useState("");
  const [budget, setBudget] = useState("");
  const [flexible, setFlexible] = useState(false);
  const [addons, setAddons] = useState([]);
  const [booking, setBooking] = useState(false);
  const [tipPct, setTipPct] = useState(0);       // 0 | 10 | 15 | 20 | "custom"
  const [customTip, setCustomTip] = useState("");
  // Availability probe — runs every time date/time changes (debounced 350ms)
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);

  useEffect(()=>{ api.get(`/experiences/${id}`).then(r=>setExp(r.data)).catch(()=>setExp(null)); },[id]);

  // Debounced availability check whenever the customer changes date/time/flexible
  useEffect(() => {
    if (!exp?.provider_id) return undefined;
    if (!date && !flexible) { setAvailability(null); return undefined; }
    const handle = setTimeout(async () => {
      setCheckingAvail(true);
      try {
        const r = await api.post("/concierge/availability/check", {
          user_id: exp.provider_id,
          date: date || new Date().toISOString().slice(0, 10),
          start_time: time || null,
          end_time: endTime || null,
          flexible,
        });
        setAvailability(r.data);
      } catch (e) {
        setAvailability(null);
      }
      setCheckingAvail(false);
    }, 350);
    return () => clearTimeout(handle);
  }, [exp?.provider_id, date, time, endTime, flexible]);

  if (!exp) return <div className="p-20 text-center">Loading experience…</div>;

  const total = exp.price + addons.reduce((s,a)=>{
    const found = exp.addons?.find(x=>x.name===a); return s + (found?.price||0);
  },0);
  const tip = tipPct === "custom"
    ? Math.max(0, parseFloat(customTip) || 0)
    : Math.round(total * (tipPct/100) * 100) / 100;
  const customerTotal = Math.round((total + tip) * 100) / 100;

  const canSubmit = !!user && (flexible || !!date) && !booking
    && (flexible || availability?.available !== false);

  const book = async () => {
    if (!user) { nav("/login"); return; }
    setBooking(true);
    try {
      const payload = {
        experience_id: exp.experience_id,
        date: flexible ? (date || new Date().toISOString().slice(0,10)) : date,
        time, end_time: endTime, guests, addons,
        notes: "", tip,
        occasion: occasion || undefined,
        budget: budget || undefined,
        flexible_dates: flexible,
      };
      if (referralHost) payload.host_id = referralHost;
      const { data } = await api.post("/bookings", payload);
      // immediate pay
      await api.post(`/bookings/${data.booking_id}/pay`);
      nav("/customer");
    } catch (e) {
      logErr("Booking error", e);
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "concierge_unavailable") {
        toast.error(detail.detail || "Concierge is unavailable for this date.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not submit booking.");
      }
    }
    setBooking(false);
  };

  // Status pill content (drives the green/amber/red banner above the CTA)
  let availStatus = null;
  if (flexible) {
    availStatus = { tone: "ok", text: "Flexible-date request — Concierge will propose a date.", icon: CalendarCheck2 };
  } else if (checkingAvail) {
    availStatus = { tone: "muted", text: "Checking availability…", icon: Clock };
  } else if (availability?.available === true) {
    availStatus = { tone: "ok", text: "Available for your requested date.", icon: CalendarCheck2 };
  } else if (availability?.available === false) {
    availStatus = {
      tone: "warn",
      text: availability.reason || "Concierge is unavailable for this date/time.",
      next: availability.next_available_date,
      icon: AlertTriangle,
    };
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] rounded-3xl overflow-hidden shadow-luxury-lg">
            <SafeExperienceImg exp={exp} alt={exp.title} className="w-full h-full object-cover"/>
          </div>
          <div className="mt-6 flex items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#064E3B]">
                <span>{exp.category}</span> · <span>{exp.moment_type}</span>
              </div>
              <h1 className="font-serif text-4xl mt-2">{exp.title}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#071A2D]/70">
                <span className="flex items-center gap-1"><MapPin size={14}/> {exp.city}, {exp.state}</span>
                <span className="flex items-center gap-1"><Clock size={14}/> {exp.duration}</span>
                <span className="flex items-center gap-1"><Users size={14}/> Up to {exp.guest_limit}</span>
                <span className="flex items-center gap-1"><Star size={14} fill="#C9A45C" color="#C9A45C"/> {exp.rating}</span>
              </div>
            </div>
          </div>

          <p className="mt-6 text-[#071A2D]/80 leading-relaxed text-lg">{exp.description}</p>

          {/* Your TREL Concierge */}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-[0.22em] text-[#064E3B] font-semibold mb-3">Your TREL Concierge</div>
            <div className="p-6 rounded-3xl card-luxury flex items-center gap-5">
              <img src={exp.provider?.photo} alt="" className="w-16 h-16 rounded-full object-cover"/>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{exp.provider?.name}{exp.city ? ` · ${exp.city} Concierge` : ""}</div>
                </div>
                <div className="mt-2">
                  <TrustBadges provider={exp.provider || {}} size="sm" />
                </div>
                <div className="text-sm text-[#071A2D]/60 mt-2">{exp.provider?.bio}</div>
              </div>
              <div className="text-right text-sm">
                <div>⭐ {exp.provider?.rating}</div>
              </div>
            </div>
          </div>

          {/* TREL Trust block — explains the visible badges */}
          {(exp.provider?.identity_verified || exp.provider?.trel_certified || exp.provider?.elite_badge_active || exp.provider?.verification_status === "Approved") && (
            <div data-testid="trust-block" className="mt-4 p-5 rounded-3xl bg-[#FFF8EE] border border-[#064E3B]/20">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#064E3B] font-semibold">
                <ShieldCheck size={12}/> Why customers trust this concierge
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                {exp.provider?.identity_verified && (
                  <div className="flex items-start gap-2"><Check size={14} className="text-[#00AEEF] mt-1 shrink-0"/><span>Identity verified through a secure verification partner.</span></div>
                )}
                {(exp.provider?.trel_certified || (exp.provider?.certification && exp.provider.certification.completed_at)) && (
                  <div className="flex items-start gap-2"><Check size={14} className="text-[#064E3B] mt-1 shrink-0"/><span>TREL Certified — completed TREL standards training.</span></div>
                )}
                {exp.provider?.elite_badge_active && (
                  <div className="flex items-start gap-2"><Check size={14} className="text-[#C9A45C] mt-1 shrink-0"/><span>TREL Elite — completed enhanced background screening.</span></div>
                )}
              </div>
              <div className="mt-3 text-[11px] text-[#071A2D]/55">
                Concierges operate as independent businesses. TREL stores no sensitive ID or background-check content.
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="mt-10">
            <h3 className="font-serif text-2xl mb-4">Reviews</h3>
            {exp.reviews?.length ? exp.reviews.map(r=>(
              <div key={r.review_id} className="p-5 rounded-2xl card-luxury mb-3">
                <div className="flex items-center gap-3">
                  <img src={r.customer_photo} alt="" className="w-9 h-9 rounded-full"/>
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-sm">⭐ {r.rating}</div>
                </div>
                <p className="mt-2 text-sm text-[#071A2D]/75">{r.text}</p>
              </div>
            )) : <div className="text-sm text-[#071A2D]/50">No reviews yet — be the first.</div>}
          </div>
        </div>

        {/* Booking sidebar */}
        <div className="lg:col-span-4">
          <div className="sticky top-24 rounded-3xl bg-[#FFF8EE] shadow-luxury-lg border border-[#071A2D]/10 p-7">
            <div className="text-3xl font-serif">${total}</div>
            <div className="text-xs text-[#071A2D]/60">starting · USD</div>

            <div className="mt-6 space-y-4">
              <label className="flex items-center gap-2 text-sm" data-testid="book-flexible-toggle">
                <input
                  type="checkbox"
                  checked={flexible}
                  onChange={(e) => setFlexible(e.target.checked)}
                  className="w-4 h-4 rounded border-[#064E3B]/40 text-[#064E3B] focus:ring-[#064E3B]"
                />
                <span className="font-medium text-[#064E3B]">My dates are flexible</span>
              </label>
              {!flexible && (
                <div>
                  <label className="text-xs uppercase tracking-widest">Date</label>
                  <input data-testid="book-date" type="date" value={date} onChange={e=>setDate(e.target.value)} className="kbd-input"/>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest">Start time</label>
                  <input data-testid="book-time" type="time" value={time} onChange={e=>setTime(e.target.value)} className="kbd-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest">End time</label>
                  <input data-testid="book-end-time" type="time" value={endTime} onChange={e=>setEndTime(e.target.value)} className="kbd-input"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest">Guests</label>
                  <input data-testid="book-guests" type="number" min="1" max={exp.guest_limit} value={guests} onChange={e=>setGuests(e.target.value)} className="kbd-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest">Budget</label>
                  <select data-testid="book-budget" value={budget} onChange={e=>setBudget(e.target.value)} className="kbd-input">
                    <option value="">Open</option>
                    <option value="<$500">Under $500</option>
                    <option value="$500-$1500">$500–$1,500</option>
                    <option value="$1500-$5000">$1,500–$5,000</option>
                    <option value="$5000+">$5,000+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest">Occasion</label>
                <input
                  data-testid="book-occasion"
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  placeholder="Anniversary, birthday, corporate offsite…"
                  className="kbd-input"
                />
              </div>

              {/* Availability banner — only when we have a probe result */}
              {availStatus && (
                <div
                  data-testid="availability-banner"
                  className={`p-3 rounded-2xl text-xs flex items-start gap-2 ${
                    availStatus.tone === "ok"
                      ? "bg-[#064E3B]/10 text-[#064E3B] border border-[#064E3B]/20"
                      : availStatus.tone === "warn"
                        ? "bg-[#FF7A45]/10 text-[#FF7A45] border border-[#FF7A45]/25"
                        : "bg-[#FFF8EE] text-[#071A2D]/65 border border-[#071A2D]/10"
                  }`}
                >
                  <availStatus.icon size={14} className="mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div>{availStatus.text}</div>
                    {availStatus.next && (
                      <button
                        data-testid="availability-pick-next"
                        onClick={() => setDate(availStatus.next)}
                        className="mt-1 underline font-semibold"
                      >
                        Pick next available — {availStatus.next}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {exp.addons?.length>0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest mb-2">Add-ons</div>
                  {exp.addons.map(a=>(
                    <label key={a.name} className="flex items-center justify-between py-1 cursor-pointer">
                      <span className="flex items-center gap-2"><input type="checkbox" checked={addons.includes(a.name)} onChange={()=>setAddons(p=>p.includes(a.name)?p.filter(x=>x!==a.name):[...p,a.name])}/> {a.name}</span>
                      <span>+${a.price}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Tip — 100% to concierge, no commission */}
              <div>
                <div className="text-xs uppercase tracking-widest mb-2">Tip your Concierge · <span className="text-[#064E3B]/60 normal-case tracking-normal">optional · 100% to your Concierge</span></div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: 0, label: "No tip" },
                    { v: 10, label: "10%" },
                    { v: 15, label: "15%" },
                    { v: 20, label: "20%" },
                    { v: "custom", label: "Custom" },
                  ].map(o => (
                    <button key={String(o.v)} type="button" data-testid={`tip-${o.v}`}
                      onClick={()=>setTipPct(o.v)}
                      className={`px-3 py-1.5 rounded-full text-xs border ${tipPct===o.v ? "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]" : "border-[#071A2D]/15"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {tipPct === "custom" && (
                  <input data-testid="tip-custom-input" type="number" min="0" step="1" value={customTip}
                    onChange={e=>setCustomTip(e.target.value)} placeholder="Custom tip ($)"
                    className="kbd-input mt-2"/>
                )}
              </div>

              {/* Totals */}
              <div className="border-t border-[#071A2D]/10 pt-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#071A2D]/70">Subtotal</span><span data-testid="book-subtotal">${total.toFixed(2)}</span></div>
                {tip > 0 && <div className="flex justify-between text-[#064E3B]"><span>Tip (100% to provider)</span><span data-testid="book-tip">${tip.toFixed(2)}</span></div>}
                <div className="flex justify-between font-semibold pt-1 border-t border-[#071A2D]/10"><span>Total</span><span data-testid="book-total">${customerTotal.toFixed(2)}</span></div>
              </div>
            </div>

            <button data-testid="book-now" onClick={book} disabled={!canSubmit} className="btn-coral w-full mt-6 disabled:opacity-60">
              {booking ? "Starting…" : (flexible ? "Send flexible request" : "Request this Concierge")}
            </button>
            {referralHost ? (
              <div className="mt-3 text-xs text-center rounded-full px-3 py-1.5 bg-[#064E3B] text-[#FFF8EE]">
                Host referral · Concierge 80% · Host 5% · TREL 15% · Tips 100% Concierge
              </div>
            ) : (
              <div className="text-xs text-[#071A2D]/50 mt-3 text-center">Secure checkout · 20% TREL fee · 80% to your Concierge · Tips 100% to your Concierge</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
